import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import RuleFolderOutlinedIcon from '@mui/icons-material/RuleFolderOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PsychologyAltOutlinedIcon from '@mui/icons-material/PsychologyAltOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { FONT_DISPLAY } from './theme';
import { CheckGlyph, CrossGlyph, Reveal, checkBulletSx } from './designSystem';

const HERO_CYCLE_MS = 4200;
const HERO_SCAN_MS = 950;
const HERO_BLOCK_HOLD_MS = 1250;
const INK = '#0B2E43';
const TEAL = '#13947F';

const heroDemoStates = [
  {
    id: 'search',
    stepLabel: 'Step 1',
    stepTitle: 'Search',
    title: 'Find the lesson',
    body: 'Normal search — nothing filtered here.',
    url: 'google.com/search?q=water+cycle+lesson',
    badge: { label: 'Not filtered', tone: 'neutral' },
    filtered: false,
  },
  {
    id: 'lesson',
    stepLabel: 'Step 2',
    stepTitle: 'Allow lesson',
    title: 'Keep the lesson open',
    body: 'Instruction stays accessible.',
    url: 'youtube.com/watch?v=teacher-assigned-video',
    badge: { label: 'Allowed', tone: 'allow' },
  },
  {
    id: 'blocked',
    stepLabel: 'Step 3',
    stepTitle: 'Block & return',
    title: 'Stop the distraction',
    body: 'The click is stopped — back to the lesson.',
    url: 'youtube.com/watch?v=distracting-recommendation',
    returnUrl: 'youtube.com/watch?v=teacher-assigned-video',
    badge: { label: 'Blocked', tone: 'block' },
    cycleMs: 6200,
  },
];

const proofPoints = [
  'Page-level decisions inside mixed-use sites',
  'Plain-language rules for specific content schools want blocked',
  'District baseline, profiles, and managed rollout',
];

const benefitCards = [
  {
    icon: <TravelExploreOutlinedIcon />,
    accent: '#C08A2D',
    tint: 'rgba(192,138,45,0.12)',
    eyebrow: 'Precision',
    title: 'Page-level control inside mixed-use sites',
    body: 'YouTube, search, and reference sites are part lesson, part distraction. Keep the assigned page open — and block the recommendation right after it.',
  },
  {
    icon: <RuleFolderOutlinedIcon />,
    accent: TEAL,
    tint: 'rgba(19,148,127,0.12)',
    eyebrow: 'Policy',
    title: 'Specific policy in plain language',
    body: 'If a school can write it in a sentence, it can be enforced — even when no traditional filtering category exists for it.',
  },
  {
    icon: <FactCheckOutlinedIcon />,
    accent: '#46729B',
    tint: 'rgba(70,114,155,0.12)',
    eyebrow: 'Oversight',
    title: 'Activity you can audit',
    body: 'Reports show every scan as allowed or blocked across the window you pick — so you can watch the policy work, and tighten the wording when a call looks off.',
  },
];

const policyVerdicts = [
  {
    label: 'Water Cycle Lab Demonstration',
    domain: 'youtube.com · assigned by the teacher',
    verdict: 'Allowed',
    tone: 'allow',
    reason: 'Clearly educational and staff-directed.',
  },
  {
    label: 'Prank Compilation',
    domain: 'youtube.com · recommended next',
    verdict: 'Blocked',
    tone: 'block',
    reason: 'Entertainment with no instructional purpose.',
  },
  {
    label: 'Printable Water Cycle Diagram',
    domain: 'scienceclassroomresources.org',
    verdict: 'Allowed',
    tone: 'allow',
    reason: 'Reference material that supports the lesson.',
  },
];

const deploymentTargets = ['Chrome Enterprise', 'Microsoft Intune', 'Jamf School'];

const filterComparisonRows = [
  {
    scenario: 'A teacher assigns a YouTube video',
    category: 'All of YouTube is one switch — block it and the lesson goes down with the noise.',
    blockweb: 'That page is allowed. It is clearly educational and staff-directed.',
  },
  {
    scenario: 'The recommended video right after it',
    category: 'Allowed — it rides in on the same domain decision as the lesson.',
    blockweb: 'Blocked at the click — the student lands right back on the lesson.',
  },
  {
    scenario: 'A distraction site that launched yesterday',
    category: 'Uncategorized, so it slips through until somebody updates a list.',
    blockweb: 'Reviewed like every other page — your written policy applies on its first visit.',
  },
  {
    scenario: 'The lab goes into a testing window',
    category: 'One global policy, with no sense of time or place.',
    blockweb: 'Profiles and class-period schedules switch the posture for that room, that period.',
  },
];

const trustPoints = [
  {
    icon: <VerifiedUserOutlinedIcon />,
    accent: TEAL,
    tint: 'rgba(19,148,127,0.12)',
    title: 'The CIPA job, done precisely',
    body: 'Schools filter because CIPA and common sense require it. Page-level decisions carry that duty without flattening instruction along the way.',
  },
  {
    icon: <ReceiptLongOutlinedIcon />,
    accent: '#46729B',
    tint: 'rgba(70,114,155,0.12)',
    title: 'Every verdict on the record',
    body: 'Each scan lands in Reports as allowed or blocked — by device, profile, and time window — so the filter’s behavior is something you can watch, not guess at.',
  },
  {
    icon: <SupervisorAccountOutlinedIcon />,
    accent: '#B07A1F',
    tint: 'rgba(223,162,58,0.16)',
    title: 'Humans outrank the AI',
    body: 'Allowlists and blocklists always win, and teachers can quick-allow a lesson page mid-class without touching district policy.',
  },
  {
    icon: <LockOutlinedIcon />,
    accent: '#0B2E43',
    tint: 'rgba(11,46,67,0.08)',
    title: 'Your workspace, your rules',
    body: 'Policies, devices, and reports live in a workspace your administrators control — what gets filtered is decided by your written policy, not an opaque vendor list.',
  },
];

const faqItems = [
  {
    question: 'Can we pilot this without replacing our current filter?',
    answer:
      'Yes. BlockWebAI handles the page-level decisions category filters cannot make, so most pilots start on a single classroom or device cart alongside existing infrastructure. The dashboard walks you through that exact path: write a baseline, add a profile, enroll devices, review reports.',
  },
  {
    question: 'What decides first — the lists or the AI?',
    answer:
      'Deterministic rules win. Allowlists and blocklists are checked first and always outrank the AI. The AI only reviews the gray-area pages in between, against the plain-language rules you wrote — and you can tighten those rules any time the reports show a call you disagree with.',
  },
  {
    question: 'Can a teacher unblock something in the middle of a lesson?',
    answer:
      'Yes. Teachers get their own console for temporary quick allows when a lesson page gets caught, so class keeps moving. District policy itself stays admin-only — a quick allow never rewrites the baseline.',
  },
  {
    question: 'What does a student see when a page is blocked?',
    answer:
      'Nothing dramatic. The student is sent straight back to the page they came from, so the lesson picks up where it left off — the off-task page simply never opens. A block screen only appears when there is no previous page to return to.',
  },
  {
    question: 'Which devices and management tools does it work with?',
    answer:
      'Rollout is built around Chrome Enterprise / Google Admin, Microsoft Intune, and Jamf School. There is also a manual enrollment path for small pilots before you wire up device management.',
  },
  {
    question: 'How does this relate to CIPA?',
    answer:
      'CIPA ties E-Rate funding to filtering obscene and harmful-to-minors content on student devices. BlockWebAI is a content filter built for that job — and because it decides page by page and records every decision, meeting the requirement does not mean blocking the lesson too. Your compliance posture is ultimately your district’s determination, but the filtering, logging, and policy controls here are designed to support it.',
  },
];

const howItWorksSteps = [
  {
    icon: <EditNoteOutlinedIcon />,
    title: 'Write the rule',
    body: 'Describe what should be allowed or blocked the way you would explain it to a colleague.',
  },
  {
    icon: <PsychologyAltOutlinedIcon />,
    title: 'AI reviews each page',
    body: 'Every page a student opens is checked against your policy — not just its domain category.',
  },
  {
    icon: <VerifiedOutlinedIcon />,
    title: 'Lessons stay open',
    body: 'Instructional content keeps flowing while off-task pages are stopped at the click.',
  },
];

function ScrollProgressBar() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let frameId = 0;
    const onScroll = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        const doc = document.documentElement;
        const max = doc.scrollHeight - window.innerHeight;
        setProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0);
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.cancelAnimationFrame(frameId);
    };
  }, []);

  return (
    <Box aria-hidden sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, zIndex: 2000, pointerEvents: 'none' }}>
      <Box
        sx={{
          height: '100%',
          width: `${progress * 100}%`,
          background: 'linear-gradient(90deg, #0E4D6E, #13947F 60%, #DFA23A)',
          transition: 'width 80ms linear',
        }}
      />
    </Box>
  );
}

function DemoSearchView() {
  return (
    <Box sx={{ display: 'grid', gap: 1.2, p: 1.8 }}>
      <Box
        sx={{
          p: 1,
          borderRadius: '12px',
          border: '1px solid rgba(11,46,67,0.1)',
          bgcolor: 'white',
          color: '#5C7689',
          fontSize: '0.78rem',
          fontFamily: '"SFMono-Regular", "Menlo", "Monaco", monospace',
        }}
      >
        water cycle lesson
      </Box>
      {[
        {
          title: 'Water Cycle Lab Demonstration',
          url: 'youtube.com/watch?v=teacher-assigned-video',
          snippet: 'Teacher-assigned science video used in the classroom.',
          highlight: true,
        },
        {
          title: 'Printable Water Cycle Diagram',
          url: 'scienceclassroomresources.org/water-cycle',
          snippet: 'Student handout and labeled diagram for the lesson.',
        },
        {
          title: 'Water Cycle for Kids',
          url: 'nationalgeographic.org/water-cycle',
          snippet: 'Background reading and short explainer for class.',
        },
      ].map((item) => (
        <Box
          key={item.title}
          sx={{
            p: 1.35,
            borderRadius: '14px',
            border: item.highlight ? '1px solid rgba(19,148,127,0.32)' : '1px solid rgba(11,46,67,0.08)',
            bgcolor: item.highlight ? 'rgba(19,148,127,0.06)' : 'white',
            boxShadow: item.highlight ? '0 10px 20px rgba(19,148,127,0.08)' : 'none',
          }}
        >
          <Typography sx={{ color: INK, fontWeight: 800, fontSize: '0.84rem' }}>{item.title}</Typography>
          <Typography sx={{ color: TEAL, fontSize: '0.72rem', mt: 0.3 }}>{item.url}</Typography>
          <Typography sx={{ color: '#64808F', fontSize: '0.76rem', mt: 0.55, lineHeight: 1.55 }}>{item.snippet}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// mode: 'lesson' (assigned video playing) | 'distraction-loading' (prank page
// opening, scan in progress) | 'distraction-blocked' (verdict landed) |
// 'returned' (bounced back to the lesson after the block)
function DemoLessonView({ mode }) {
  const lessonView = mode === 'lesson' || mode === 'returned';
  const blockedOverlay = mode === 'distraction-blocked';
  const nowPlaying = lessonView ? 'Water Cycle Lab Demonstration' : 'Prank Compilation';
  const distractionItems = lessonView
    ? ['Prank Compilation', 'Celebrity Drama Recap', 'Gaming Highlights']
    : ['Celebrity Drama Recap', 'Gaming Highlights', 'More Like This'];

  return (
    <Box>
      {mode === 'returned' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.8,
            py: 0.9,
            bgcolor: 'rgba(19,148,127,0.1)',
            borderBottom: '1px solid rgba(19,148,127,0.22)',
          }}
        >
          <Box sx={{ ...checkBulletSx, width: 18, height: 18, mt: 0 }}>
            <CheckGlyph />
          </Box>
          <Typography sx={{ color: '#0C6E5E', fontSize: '0.74rem', fontWeight: 700 }}>
            Off-task page blocked — back to the lesson, right where it left off.
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1.45fr 0.95fr', minHeight: 352 }}>
      <Box sx={{ p: 1.8, bgcolor: 'white' }}>
        <Box
          sx={{
            position: 'relative',
            aspectRatio: '16 / 9',
            borderRadius: '14px',
            overflow: 'hidden',
            background: lessonView
              ? 'linear-gradient(135deg, #0B2E43 0%, #16607D 54%, #1A7E6B 100%)'
              : 'linear-gradient(135deg, #1c0f1e 0%, #3a1430 60%, #2f120e 100%)',
            boxShadow: 'inset 0 -48px 90px rgba(0,0,0,0.22)',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />
          {!blockedOverlay && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
              <Box
                sx={{
                  width: 62,
                  height: 62,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.18)',
                  border: '1px solid rgba(255,255,255,0.38)',
                  backdropFilter: 'blur(8px)',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 0,
                    height: 0,
                    borderTop: '11px solid transparent',
                    borderBottom: '11px solid transparent',
                    borderLeft: '17px solid white',
                    ml: '4px',
                  }}
                />
              </Box>
            </Box>
          )}
          {blockedOverlay && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(0,0,0,0.56)',
                backdropFilter: 'blur(7px)',
              }}
            >
              <Stack alignItems="center" spacing={1.15}>
                <Box
                  sx={{
                    width: 58,
                    height: 58,
                    borderRadius: '50%',
                    bgcolor: 'rgba(190,71,53,0.2)',
                    border: '1px solid rgba(190,71,53,0.42)',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <CrossGlyph color="#f08d78" size={22} strokeWidth={2.5} />
                </Box>
                <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.92rem' }}>Off-task page blocked</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.46)', fontSize: '0.74rem' }}>
                  Returning to the lesson…
                </Typography>
              </Stack>
            </Box>
          )}
          <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, p: 1.5, background: 'linear-gradient(transparent, rgba(0,0,0,0.78))' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.52)', fontSize: '0.64rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              Now Playing
            </Typography>
            <Typography sx={{ color: 'white', fontWeight: 700, mt: 0.4, fontSize: '0.86rem' }}>
              {nowPlaying}
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mt: 1.3 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: INK,
                fontWeight: 800,
                fontSize: '0.88rem',
                lineHeight: 1.35,
                filter: blockedOverlay ? 'blur(2.5px)' : 'none',
                transition: 'filter 240ms ease',
              }}
            >
              {nowPlaying}
            </Typography>
            <Typography sx={{ mt: 0.5, color: '#64808F', fontSize: '0.77rem', lineHeight: 1.6, filter: blockedOverlay ? 'blur(2.5px)' : 'none' }}>
              {lessonView
                ? 'Teacher-assigned science video stays accessible for the class.'
                : 'Viral entertainment clips and unrelated recommendations.'}
            </Typography>
          </Box>
          {mode !== 'distraction-loading' && (
            <Chip
              size="small"
              label={blockedOverlay ? 'Blocked' : 'Allowed'}
              sx={
                blockedOverlay
                  ? { bgcolor: 'rgba(192,69,49,0.12)', color: '#9A3322', fontWeight: 800, flexShrink: 0 }
                  : { bgcolor: 'rgba(19,148,127,0.12)', color: '#0C6E5E', fontWeight: 800, flexShrink: 0 }
              }
            />
          )}
        </Stack>
      </Box>

      <Box sx={{ p: 1.55, bgcolor: '#F4F8FA', borderLeft: '1px solid rgba(11,46,67,0.08)' }}>
        <Box
          sx={{
            p: 1.2,
            borderRadius: '12px',
            bgcolor: 'rgba(19,148,127,0.08)',
            border: '1px solid rgba(19,148,127,0.18)',
            mb: 1.35,
          }}
        >
          <Typography sx={{ color: TEAL, fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.5 }}>
            Policy
          </Typography>
          <Typography sx={{ color: '#3B6170', fontSize: '0.72rem', lineHeight: 1.52 }}>
            Block games and entertainment unless clearly educational or staff-directed.
          </Typography>
        </Box>
        <Typography sx={{ color: '#8CA9B7', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.75 }}>
          Up Next
        </Typography>
        <Stack spacing={0.78}>
          {distractionItems.map((label, index) => {
            const markBlocked = mode === 'returned' && index === 0;
            return (
              <Box
                key={label}
                sx={{
                  p: 0.95,
                  borderRadius: '10px',
                  bgcolor: 'white',
                  border: markBlocked ? '1px solid rgba(192,69,49,0.28)' : '1px solid rgba(11,46,67,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    color: markBlocked ? '#9A3322' : '#6B8290',
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    textDecoration: markBlocked ? 'line-through' : 'none',
                  }}
                >
                  {label}
                </Typography>
                {markBlocked && (
                  <Typography sx={{ color: '#9A3322', fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', flexShrink: 0 }}>
                    Blocked
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
    </Box>
  );
}

function LandingPage() {
  const [stateIndex, setStateIndex] = useState(0);
  // Each step: scanning -> verdict; the blocked step adds -> returned (bounced
  // back to the lesson), mirroring the real product behavior.
  const [phase, setPhase] = useState('scanning');
  const [paused, setPaused] = useState(false);
  const activeState = heroDemoStates[stateIndex];
  const scanning = phase === 'scanning';
  const returned = phase === 'returned';
  const isBlockedStep = activeState.id === 'blocked';
  const cycleMs = activeState.cycleMs || HERO_CYCLE_MS;

  useEffect(() => {
    if (paused) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setStateIndex((current) => (current + 1) % heroDemoStates.length);
    }, heroDemoStates[stateIndex].cycleMs || HERO_CYCLE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [paused, stateIndex]);

  useEffect(() => {
    // Search results aren't filtered by the product, so that step skips the
    // scan entirely and just shows a neutral state.
    if (heroDemoStates[stateIndex].filtered === false) {
      setPhase('idle');
      return undefined;
    }

    setPhase('scanning');
    const verdictId = window.setTimeout(() => setPhase('verdict'), HERO_SCAN_MS);
    const returnId =
      heroDemoStates[stateIndex].id === 'blocked'
        ? window.setTimeout(() => setPhase('returned'), HERO_SCAN_MS + HERO_BLOCK_HOLD_MS)
        : undefined;

    return () => {
      window.clearTimeout(verdictId);
      if (returnId) {
        window.clearTimeout(returnId);
      }
    };
  }, [stateIndex]);

  const ALLOW_CHIP_SX = { bgcolor: 'rgba(19,148,127,0.14)', color: '#0C6E5E' };
  const BLOCK_CHIP_SX = { bgcolor: 'rgba(192,69,49,0.14)', color: '#9A3322' };

  const chip =
    phase === 'idle'
      ? { label: 'Not filtered', sx: { bgcolor: 'rgba(11,46,67,0.08)', color: '#52677A' } }
      : scanning
        ? { label: 'Scanning…', sx: { bgcolor: 'rgba(70,114,155,0.14)', color: '#3E6488' } }
        : isBlockedStep
          ? returned
            ? { label: 'Back to lesson', sx: ALLOW_CHIP_SX }
            : { label: 'Blocked', sx: BLOCK_CHIP_SX }
          : { label: activeState.badge.label, sx: activeState.badge.tone === 'allow' ? ALLOW_CHIP_SX : BLOCK_CHIP_SX };

  const toast =
    scanning || phase === 'idle'
      ? null
      : isBlockedStep
        ? returned
          ? { tone: 'allow', kicker: 'Blocked → Returned', text: 'Back to the lesson' }
          : { tone: 'block', kicker: 'Blocked', text: 'Prank Compilation' }
        : { tone: 'allow', kicker: 'Allowed', text: 'Teacher-assigned lesson' };

  const displayUrl = isBlockedStep && returned ? activeState.returnUrl : activeState.url;

  const demoMode =
    activeState.id === 'search'
      ? 'search'
      : activeState.id === 'lesson'
        ? 'lesson'
        : returned
          ? 'returned'
          : phase === 'verdict'
            ? 'distraction-blocked'
            : 'distraction-loading';

  return (
    <Box sx={{ color: INK }}>
      <ScrollProgressBar />
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(150deg, #071D2C 0%, #0B3148 55%, #0C2A3E 100%)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            maskImage: 'radial-gradient(ellipse at 30% 20%, black 30%, transparent 75%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: -180,
            left: -120,
            width: 560,
            height: 560,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(19,148,127,0.24), transparent 68%)',
            pointerEvents: 'none',
            animation: 'auroraDrift 16s ease-in-out infinite alternate',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            right: -140,
            top: 40,
            width: 420,
            height: 420,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(223,162,58,0.12), transparent 66%)',
            pointerEvents: 'none',
            animation: 'auroraDrift 21s ease-in-out 2s infinite alternate-reverse',
          }}
        />

        <Container sx={{ py: { xs: 8, md: 12 }, position: 'relative' }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6.6}>
              <Stack spacing={3.2} sx={{ animation: 'fadeRiseIn 600ms ease both' }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.68,
                    borderRadius: '999px',
                    border: '1px solid rgba(19,148,127,0.4)',
                    bgcolor: 'rgba(19,148,127,0.14)',
                    width: 'fit-content',
                  }}
                >
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#2FBFA4', animation: 'softPulse 2.4s ease-in-out infinite' }} />
                  <Typography sx={{ color: '#7EE8D6', fontSize: '0.74rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                    Adaptive school filtering
                  </Typography>
                </Box>

                <Typography
                  variant="h1"
                  sx={{
                    color: 'white',
                    fontSize: { xs: '2.7rem', sm: '3.3rem', md: '4.05rem' },
                    lineHeight: 1.04,
                    maxWidth: 700,
                  }}
                >
                  Keep the lesson.{' '}
                  <Box
                    component="span"
                    sx={{
                      background: 'linear-gradient(100deg, #5EDFC8 0%, #8BE9D8 40%, #E8C375 60%, #5EDFC8 100%)',
                      backgroundSize: '220% 100%',
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      color: 'transparent',
                      animation: 'gradientShift 7s ease-in-out infinite alternate',
                    }}
                  >
                    Block exactly
                  </Box>{' '}
                  what does not belong.
                </Typography>

                <Typography
                  sx={{
                    maxWidth: 600,
                    color: 'rgba(255,255,255,0.68)',
                    fontSize: { xs: '1rem', md: '1.12rem' },
                    lineHeight: 1.72,
                  }}
                >
                  Page-by-page filtering for mixed-use sites, plus plain-language rules for the specific things a school wants blocked — even when no old category fits.
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.75}>
                  <Button
                    variant="contained"
                    size="large"
                    component={Link}
                    to="/register"
                    endIcon={<ArrowForwardRoundedIcon />}
                    sx={{
                      background: '#ffffff',
                      color: '#0B2E43',
                      fontWeight: 750,
                      boxShadow: '0 16px 34px rgba(0,0,0,0.28)',
                      '&:hover': { background: 'rgba(255,255,255,0.92)', transform: 'translateY(-1px)' },
                    }}
                  >
                    Start a School Pilot
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    component={Link}
                    to="/demo"
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.28)',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.55)',
                        bgcolor: 'rgba(255,255,255,0.06)',
                      },
                    }}
                  >
                    Try the Live Demo
                  </Button>
                </Stack>

                <Stack spacing={1.15} sx={{ pt: 0.6 }}>
                  {proofPoints.map((point) => (
                    <Stack key={point} direction="row" spacing={1.2} alignItems="flex-start">
                      <Box sx={{ ...checkBulletSx, bgcolor: 'rgba(19,148,127,0.22)', border: '1px solid rgba(19,148,127,0.45)' }}>
                        <CheckGlyph color="#7EE8D6" />
                      </Box>
                      <Typography sx={{ color: 'rgba(255,255,255,0.74)', fontSize: '0.97rem' }}>{point}</Typography>
                    </Stack>
                  ))}
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ pt: 1 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.42)', fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', mr: 0.5 }}>
                    Deploys via
                  </Typography>
                  {deploymentTargets.map((target) => (
                    <Box
                      key={target}
                      sx={{
                        px: 1.4,
                        py: 0.5,
                        borderRadius: '999px',
                        border: '1px solid rgba(255,255,255,0.16)',
                        bgcolor: 'rgba(255,255,255,0.05)',
                        color: 'rgba(255,255,255,0.72)',
                        fontSize: '0.78rem',
                        fontWeight: 650,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {target}
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5.4}>
              <Box sx={{ position: 'relative', animation: 'fadeRiseIn 700ms 120ms ease both' }}>
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    inset: -36,
                    background:
                      'radial-gradient(circle at 28% 22%, rgba(19,148,127,0.4), transparent 58%), radial-gradient(circle at 82% 82%, rgba(223,162,58,0.22), transparent 55%)',
                    filter: 'blur(34px)',
                    zIndex: 0,
                    pointerEvents: 'none',
                  }}
                />
                <Box
                  onMouseEnter={() => setPaused(true)}
                  onMouseLeave={() => setPaused(false)}
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    borderRadius: '22px',
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.16)',
                    bgcolor: '#F7FBFC',
                    boxShadow: '0 30px 60px -12px rgba(0,0,0,0.5), 0 90px 160px -60px rgba(0,0,0,0.6)',
                    transform: { md: 'perspective(1400px) rotateY(-6deg) rotateX(2deg)' },
                    transition: 'transform 480ms ease',
                    '&:hover': {
                      transform: { md: 'perspective(1400px) rotateY(0deg) rotateX(0deg)' },
                    },
                  }}
                >
                <Box
                  sx={{
                    px: 2,
                    py: 1.2,
                    borderBottom: '1px solid rgba(11,46,67,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    bgcolor: '#EEF4F6',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.7 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#EF8F7F' }} />
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#E6C15F' }} />
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#68C08E' }} />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      px: 1.5,
                      py: 0.88,
                      borderRadius: '999px',
                      bgcolor: 'white',
                      color: '#6A8290',
                      fontSize: '0.8rem',
                      fontFamily: '"SFMono-Regular", "Menlo", "Monaco", monospace',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {displayUrl}
                  </Box>
                  <Chip
                    key={`${stateIndex}-${phase}`}
                    label={chip.label}
                    size="small"
                    sx={{
                      ...chip.sx,
                      fontWeight: 800,
                      width: 118,
                      justifyContent: 'center',
                      animation: scanning ? 'none' : 'popIn 320ms ease both',
                    }}
                  />
                </Box>

                <Box sx={{ position: 'relative' }}>
                  <Box key={`${stateIndex}-${demoMode}`} sx={{ animation: 'viewEnter 420ms ease both' }}>
                    {demoMode === 'search' ? <DemoSearchView /> : <DemoLessonView mode={demoMode} />}
                  </Box>
                  {scanning && (
                    <Box aria-hidden sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 2 }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          height: 110,
                          background: 'linear-gradient(180deg, transparent 0%, rgba(19,148,127,0.12) 55%, rgba(19,148,127,0.38) 100%)',
                          borderBottom: '2px solid rgba(19,148,127,0.7)',
                          boxShadow: '0 6px 22px rgba(19,148,127,0.35)',
                          animation: `scanSweep ${HERO_SCAN_MS}ms linear both`,
                        }}
                      />
                    </Box>
                  )}
                </Box>

                <Box sx={{ px: 2, py: 1.45, borderTop: '1px solid rgba(11,46,67,0.08)', bgcolor: 'white' }}>
                  <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: '#8199A5', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        {activeState.stepLabel}
                      </Typography>
                      <Typography sx={{ color: INK, fontWeight: 800, mt: 0.32, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {activeState.stepTitle}
                      </Typography>
                      <Typography sx={{ color: '#708894', fontSize: '0.74rem', mt: 0.28, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {activeState.body}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.7} flexShrink={0}>
                      {heroDemoStates.map((state, index) => {
                        const stateColor = state.badge.tone === 'block' ? '#C04531' : state.badge.tone === 'allow' ? TEAL : '#46729B';
                        return (
                          <Box
                            key={state.id}
                            component="button"
                            type="button"
                            aria-label={`Show ${state.stepLabel}: ${state.stepTitle}`}
                            onClick={() => setStateIndex(index)}
                            sx={{
                              width: index === stateIndex ? 30 : 12,
                              height: 7,
                              p: 0,
                              border: 0,
                              borderRadius: '999px',
                              cursor: 'pointer',
                              overflow: 'hidden',
                              backgroundColor: 'rgba(11,46,67,0.12)',
                              transition: 'width 340ms ease',
                            }}
                          >
                            {index === stateIndex && (
                              <Box
                                key={`fill-${stateIndex}`}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  borderRadius: '999px',
                                  bgcolor: stateColor,
                                  transformOrigin: 'left',
                                  animation: `fillBar ${cycleMs}ms linear both`,
                                  animationPlayState: paused ? 'paused' : 'running',
                                }}
                              />
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  </Stack>
                </Box>
                </Box>

                {toast && (
                  <Box
                    key={`toast-${stateIndex}-${phase}`}
                    sx={{
                      position: 'absolute',
                      top: -20,
                      right: { md: -10, lg: -26 },
                      zIndex: 2,
                      display: { xs: 'none', md: 'flex' },
                      alignItems: 'center',
                      gap: 1.25,
                      px: 1.75,
                      py: 1.1,
                      borderRadius: '14px',
                      bgcolor: 'rgba(9,30,43,0.88)',
                      border: '1px solid rgba(255,255,255,0.14)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 18px 40px -12px rgba(0,0,0,0.5)',
                      animation: 'popIn 360ms ease both',
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: toast.tone === 'block' ? 'rgba(192,69,49,0.22)' : 'rgba(19,148,127,0.24)',
                        border: toast.tone === 'block' ? '1px solid rgba(240,141,120,0.45)' : '1px solid rgba(126,232,214,0.45)',
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {toast.tone === 'block' ? <CrossGlyph color="#F08D78" /> : <CheckGlyph color="#7EE8D6" />}
                    </Box>
                    <Box>
                      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                        {toast.kicker}
                      </Typography>
                      <Typography sx={{ color: 'white', fontWeight: 750, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {toast.text}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
        <Box
          aria-hidden
          sx={{
            height: '1px',
            background: 'linear-gradient(90deg, transparent 8%, rgba(126,232,214,0.35) 50%, transparent 92%)',
          }}
        />
      </Box>

      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #E6EDF2' }}>
        <Container sx={{ py: { xs: 6, md: 8 } }}>
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Reveal>
              <Typography variant="overline" sx={{ color: TEAL }}>
                Policy, not categories
              </Typography>
              <Typography variant="h2" sx={{ mt: 1, color: INK, fontSize: { xs: '1.9rem', md: '2.4rem' } }}>
                Write the rule the way you'd say it.
              </Typography>
              <Typography sx={{ mt: 2, color: '#52677A', lineHeight: 1.75, maxWidth: 440 }}>
                One sentence of plain language becomes live policy. The AI reads every page a student opens and applies your words — to the page itself, not just the domain it lives on.
              </Typography>
              <Box
                sx={{
                  mt: 3,
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: '1px solid #DCE7EE',
                  bgcolor: '#ffffff',
                  boxShadow: '0 2px 4px rgba(11,46,67,0.04), 0 24px 48px -24px rgba(11,46,67,0.25)',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ px: 2, py: 1.25, borderBottom: '1px solid #EBF2F6', bgcolor: '#F7FAFC' }}
                >
                  <Box sx={{ display: 'flex', gap: 0.6 }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#EF8F7F' }} />
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#E6C15F' }} />
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#68C08E' }} />
                  </Box>
                  <Typography sx={{ pl: 0.5, fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.12em', color: '#8CA3B2', textTransform: 'uppercase' }}>
                    Policy editor
                  </Typography>
                  <Chip
                    size="small"
                    label="Live"
                    sx={{ ml: 'auto !important', bgcolor: 'rgba(19,148,127,0.12)', color: '#0C6E5E', fontWeight: 800, height: 22 }}
                  />
                </Stack>
                <Box sx={{ px: 2.5, py: 2.25 }}>
                  <Typography
                    sx={{
                      color: '#244657',
                      fontSize: '0.95rem',
                      lineHeight: 1.7,
                      fontFamily: '"SFMono-Regular", "Menlo", "Monaco", monospace',
                    }}
                  >
                    Block games and entertainment unless clearly educational or staff-directed.
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        width: '2px',
                        height: '1.05em',
                        bgcolor: '#13947F',
                        ml: '3px',
                        verticalAlign: 'text-bottom',
                        animation: 'blinkCursor 1.1s step-end infinite',
                      }}
                    />
                  </Typography>
                </Box>
              </Box>
              </Reveal>
            </Grid>
            <Grid item xs={12} md={7}>
              <Reveal delay={140}>
              <Stack spacing={1.5}>
                {policyVerdicts.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      p: 2,
                      borderRadius: '14px',
                      border: '1px solid',
                      borderColor: item.tone === 'allow' ? 'rgba(19,148,127,0.24)' : 'rgba(192,69,49,0.24)',
                      bgcolor: '#FAFCFD',
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
                      gap: 1.5,
                      alignItems: 'center',
                      transition: 'transform 200ms ease, box-shadow 200ms ease',
                      '&:hover': {
                        transform: 'translateX(4px)',
                        boxShadow: '0 12px 28px -16px rgba(11,46,67,0.2)',
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1.5} sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          flexShrink: 0,
                          mt: 0.25,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: item.tone === 'allow' ? 'rgba(19,148,127,0.12)' : 'rgba(192,69,49,0.1)',
                          border: item.tone === 'allow' ? '1px solid rgba(19,148,127,0.3)' : '1px solid rgba(192,69,49,0.28)',
                        }}
                      >
                        {item.tone === 'allow' ? <CheckGlyph /> : <CrossGlyph />}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: INK, fontWeight: 750, fontSize: '0.95rem' }}>{item.label}</Typography>
                        <Typography sx={{ color: '#8CA3B2', fontSize: '0.78rem', mt: 0.25 }}>{item.domain}</Typography>
                        <Typography sx={{ color: '#52677A', fontSize: '0.84rem', mt: 0.6, fontStyle: 'italic' }}>
                          “{item.reason}”
                        </Typography>
                      </Box>
                    </Stack>
                    <Chip
                      label={item.verdict}
                      sx={{
                        bgcolor: item.tone === 'allow' ? 'rgba(19,148,127,0.12)' : 'rgba(192,69,49,0.12)',
                        color: item.tone === 'allow' ? '#0C6E5E' : '#9A3322',
                        fontWeight: 800,
                        justifySelf: { xs: 'start', sm: 'end' },
                      }}
                    />
                  </Box>
                ))}
              </Stack>
              </Reveal>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container sx={{ py: { xs: 7, md: 10 } }}>
        <Reveal>
          <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: { xs: 4, md: 6 } }}>
            <Typography variant="overline" sx={{ color: TEAL }}>
              Built for school policy teams
            </Typography>
            <Typography variant="h2" sx={{ mt: 1, color: INK, fontSize: { xs: '1.9rem', md: '2.5rem' } }}>
              Three things basic filters usually miss.
            </Typography>
          </Box>
        </Reveal>

        <Grid container spacing={3}>
          {benefitCards.map((card, cardIndex) => (
            <Grid item xs={12} md={4} key={card.title}>
              <Reveal delay={cardIndex * 110} sx={{ height: '100%' }}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: '20px',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'transform 220ms ease, box-shadow 220ms ease',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${card.accent}, transparent 80%)`,
                  },
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 2px 4px rgba(11,46,67,0.05), 0 28px 56px -24px rgba(11,46,67,0.22)',
                  },
                  '&:hover .benefit-icon': {
                    transform: 'scale(1.1) rotate(-4deg)',
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 3.25 } }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Box
                      className="benefit-icon"
                      sx={{
                        width: 54,
                        height: 54,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: '16px',
                        bgcolor: card.tint,
                        color: card.accent,
                        flexShrink: 0,
                        border: `1px solid ${card.accent}33`,
                        boxShadow: `0 10px 22px -10px ${card.accent}55`,
                        transition: 'transform 260ms ease',
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Typography sx={{ color: card.accent, fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                      {card.eyebrow}
                    </Typography>
                  </Stack>
                  <Typography variant="h4" sx={{ mb: 1.2, color: INK, fontSize: '1.22rem' }}>
                    {card.title}
                  </Typography>
                  <Typography sx={{ color: '#52677A', lineHeight: 1.7, fontSize: '0.95rem' }}>{card.body}</Typography>
                </CardContent>
              </Card>
              </Reveal>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ bgcolor: 'white', borderTop: '1px solid #E6EDF2', borderBottom: '1px solid #E6EDF2' }}>
        <Container sx={{ py: { xs: 7, md: 9 } }}>
          <Reveal>
            <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: { xs: 4, md: 6 } }}>
              <Typography variant="overline" sx={{ color: TEAL }}>
                Why page-level
              </Typography>
              <Typography variant="h2" sx={{ mt: 1, color: INK, fontSize: { xs: '1.9rem', md: '2.5rem' } }}>
                A category filter decides once per site.{' '}
                <Box component="span" sx={{ color: TEAL }}>
                  BlockWebAI decides every page.
                </Box>
              </Typography>
            </Box>
          </Reveal>

          <Reveal delay={120}>
          <Box
            sx={{
              border: '1px solid #E6EDF2',
              borderRadius: '20px',
              overflow: 'hidden',
              bgcolor: 'white',
              boxShadow: '0 2px 4px rgba(11,46,67,0.04), 0 28px 56px -32px rgba(11,46,67,0.2)',
            }}
          >
            <Box
              sx={{
                display: { xs: 'none', md: 'grid' },
                gridTemplateColumns: '1.05fr 1fr 1.1fr',
                bgcolor: '#F7FAFC',
                borderBottom: '1px solid #E6EDF2',
              }}
            >
              <Typography sx={{ px: 3, py: 1.75, fontWeight: 800, fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8CA3B2' }}>
                The moment
              </Typography>
              <Typography sx={{ px: 3, py: 1.75, fontWeight: 800, fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8CA3B2', borderLeft: '1px solid #E6EDF2' }}>
                Category filter
              </Typography>
              <Typography sx={{ px: 3, py: 1.75, fontWeight: 800, fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#0C6E5E', borderLeft: '1px solid #E6EDF2', bgcolor: 'rgba(19,148,127,0.07)' }}>
                BlockWebAI
              </Typography>
            </Box>
            {filterComparisonRows.map((row, rowIndex) => (
              <Box
                key={row.scenario}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr 1.1fr' },
                  borderTop: rowIndex === 0 ? 0 : '1px solid #EBF1F5',
                }}
              >
                <Box sx={{ px: 3, pt: 2.5, pb: { xs: 0.5, md: 2.5 } }}>
                  <Typography sx={{ color: INK, fontWeight: 750, lineHeight: 1.45 }}>{row.scenario}</Typography>
                </Box>
                <Box sx={{ px: 3, pt: { xs: 1.5, md: 2.5 }, pb: { xs: 0.5, md: 2.5 }, borderLeft: { md: '1px solid #EBF1F5' } }}>
                  <Typography sx={{ display: { md: 'none' }, fontWeight: 800, fontSize: '0.62rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8CA3B2', mb: 0.75 }}>
                    Category filter
                  </Typography>
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Box sx={{ ...checkBulletSx, bgcolor: 'rgba(11,46,67,0.05)', border: '1px solid rgba(11,46,67,0.14)' }}>
                      <CrossGlyph color="#7E96A7" size={9} />
                    </Box>
                    <Typography sx={{ color: '#52677A', fontSize: '0.92rem', lineHeight: 1.6 }}>{row.category}</Typography>
                  </Stack>
                </Box>
                <Box
                  sx={{
                    px: 3,
                    pt: { xs: 1.5, md: 2.5 },
                    pb: 2.5,
                    borderLeft: { md: '1px solid #EBF1F5' },
                    bgcolor: { md: 'rgba(19,148,127,0.045)' },
                  }}
                >
                  <Typography sx={{ display: { md: 'none' }, fontWeight: 800, fontSize: '0.62rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#0C6E5E', mb: 0.75 }}>
                    BlockWebAI
                  </Typography>
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Box sx={checkBulletSx}>
                      <CheckGlyph />
                    </Box>
                    <Typography sx={{ color: '#3E5A6C', fontSize: '0.92rem', lineHeight: 1.6, fontWeight: 600 }}>{row.blockweb}</Typography>
                  </Stack>
                </Box>
              </Box>
            ))}
          </Box>
          <Typography sx={{ textAlign: 'center', color: '#8CA3B2', fontSize: '0.88rem', mt: 2.5 }}>
            Allowlists and blocklists still decide instantly — the AI covers everything in between.
          </Typography>
          </Reveal>
        </Container>
      </Box>

      <Container sx={{ py: { xs: 7, md: 10 } }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          <Grid item xs={12} md={5}>
            <Reveal>
            <Typography variant="overline" sx={{ color: TEAL }}>
              Accountability &amp; privacy
            </Typography>
            <Typography variant="h2" sx={{ mt: 1, color: INK, fontSize: { xs: '1.9rem', md: '2.4rem' } }}>
              Built to answer to principals, parents, and IT.
            </Typography>
            <Typography sx={{ mt: 2, color: '#52677A', lineHeight: 1.75, maxWidth: 440 }}>
              A school filter is infrastructure. It has to hold up when somebody asks a hard question — about a blocked page, a student's screen, or where the data lives. Every layer here is designed so you can show your work.
            </Typography>
            <Button variant="outlined" component={Link} to="/privacy" sx={{ mt: 3 }}>
              Read the Privacy Policy
            </Button>
            </Reveal>
          </Grid>
          <Grid item xs={12} md={7}>
            <Grid container spacing={2}>
              {trustPoints.map((point, pointIndex) => (
                <Grid item xs={12} sm={6} key={point.title}>
                  <Reveal delay={pointIndex * 100} sx={{ height: '100%' }}>
                  <Box
                    sx={{
                      height: '100%',
                      p: 2.5,
                      borderRadius: '18px',
                      border: '1px solid #E2EAF0',
                      bgcolor: 'white',
                      boxShadow: '0 1px 2px rgba(11,46,67,0.04)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '12px',
                        display: 'grid',
                        placeItems: 'center',
                        bgcolor: point.tint,
                        color: point.accent,
                        mb: 1.5,
                      }}
                    >
                      {point.icon}
                    </Box>
                    <Typography sx={{ color: INK, fontWeight: 750, mb: 0.75 }}>{point.title}</Typography>
                    <Typography sx={{ color: '#52677A', fontSize: '0.9rem', lineHeight: 1.65 }}>{point.body}</Typography>
                  </Box>
                  </Reveal>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>

      <Box sx={{ bgcolor: 'white', borderTop: '1px solid #E6EDF2', borderBottom: '1px solid #E6EDF2' }}>
        <Container sx={{ py: { xs: 7, md: 9 } }}>
          <Reveal>
            <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: { xs: 4, md: 6 } }}>
              <Typography variant="overline" sx={{ color: TEAL }}>
                How it works
              </Typography>
              <Typography variant="h2" sx={{ mt: 1, color: INK, fontSize: { xs: '1.9rem', md: '2.5rem' } }}>
                Policy in a sentence. Enforcement on every page.
              </Typography>
            </Box>
          </Reveal>

          <Box sx={{ position: 'relative' }}>
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                top: 29,
                left: '14%',
                right: '14%',
                height: 2,
                background: 'linear-gradient(90deg, rgba(19,148,127,0.1), rgba(19,148,127,0.45) 50%, rgba(19,148,127,0.1))',
                display: { xs: 'none', md: 'block' },
              }}
            />
            <Grid container spacing={{ xs: 4, md: 3 }}>
              {howItWorksSteps.map((step, index) => (
                <Grid item xs={12} md={4} key={step.title}>
                  <Reveal delay={index * 130}>
                  <Stack
                    spacing={2}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    sx={{ textAlign: { xs: 'left', md: 'center' }, position: 'relative' }}
                  >
                    <Box
                      sx={{
                        width: 58,
                        height: 58,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        background: 'linear-gradient(140deg, #16A38C 0%, #0F7E66 100%)',
                        color: 'white',
                        boxShadow: '0 0 0 8px #ffffff, 0 14px 28px -10px rgba(19,148,127,0.55)',
                        zIndex: 1,
                      }}
                    >
                      {step.icon}
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: FONT_DISPLAY,
                          fontWeight: 800,
                          fontSize: '0.72rem',
                          letterSpacing: '0.16em',
                          color: '#8CA3B2',
                          mb: 0.75,
                        }}
                      >
                        STEP {index + 1}
                      </Typography>
                      <Typography variant="h5" sx={{ color: INK, mb: 1, fontSize: '1.22rem' }}>
                        {step.title}
                      </Typography>
                      <Typography sx={{ color: '#52677A', lineHeight: 1.7, maxWidth: 300, mx: { md: 'auto' } }}>
                        {step.body}
                      </Typography>
                    </Box>
                  </Stack>
                  </Reveal>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      <Container sx={{ py: { xs: 7, md: 9 } }}>
        <Reveal>
          <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: { xs: 4, md: 5 } }}>
            <Typography variant="overline" sx={{ color: TEAL }}>
              Before the demo call
            </Typography>
            <Typography variant="h2" sx={{ mt: 1, color: INK, fontSize: { xs: '1.9rem', md: '2.5rem' } }}>
              Questions schools actually ask.
            </Typography>
          </Box>
        </Reveal>
        <Reveal delay={100}>
        <Stack spacing={1.5} sx={{ maxWidth: 780, mx: 'auto' }}>
          {faqItems.map((item) => (
            <Accordion key={item.question}>
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon sx={{ color: '#8CA3B2' }} />}>
                <Typography sx={{ color: INK, fontWeight: 750 }}>{item.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: '#52677A', lineHeight: 1.75 }}>{item.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
        </Reveal>
        <Typography sx={{ textAlign: 'center', color: '#52677A', mt: 4 }}>
          Something else?{' '}
          <Box component={Link} to="/contact" sx={{ color: '#0E4D6E', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            Ask us directly
          </Box>{' '}
          — pilot questions get answered by the people building the product.
        </Typography>
      </Container>

      <Container sx={{ pb: { xs: 7, md: 9 } }}>
        <Reveal>
        <Card
          sx={{
            borderRadius: '24px',
            border: '1px solid rgba(255,255,255,0.08)',
            overflow: 'hidden',
            position: 'relative',
            background:
              'radial-gradient(circle at 88% -10%, rgba(19,148,127,0.4), transparent 44%), radial-gradient(circle at -8% 110%, rgba(14,77,110,0.7), transparent 52%), linear-gradient(160deg, #0B2E43 0%, #103E59 100%)',
            boxShadow: '0 32px 72px -28px rgba(11,46,67,0.5)',
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
              backgroundSize: '44px 44px',
              maskImage: 'radial-gradient(ellipse at 25% 30%, black 25%, transparent 75%)',
              pointerEvents: 'none',
            }}
          />
          <CardContent sx={{ p: { xs: 4, md: 6 }, position: 'relative' }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7.5}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '12px',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      color: '#7EE8D6',
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <DevicesOutlinedIcon sx={{ fontSize: 22 }} />
                  </Box>
                  <Typography sx={{ color: '#7EE8D6', fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                    Built for school rollout
                  </Typography>
                </Stack>
                <Typography variant="h3" sx={{ color: 'white', fontSize: { xs: '1.7rem', md: '2.2rem' }, mb: 1.5 }}>
                  From one pilot classroom to a managed district fleet.
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.66)', lineHeight: 1.75, maxWidth: 560 }}>
                  District baseline, classroom profiles, class-period schedules, teacher quick allows, and managed-device rollout through Chrome Enterprise, Intune, or Jamf.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4.5}>
                <Stack spacing={1.5} sx={{ width: '100%' }}>
                  <Button
                    variant="contained"
                    size="large"
                    component={Link}
                    to="/register"
                    sx={{
                      background: '#ffffff',
                      color: '#0B2E43',
                      fontWeight: 750,
                      '&:hover': { background: 'rgba(255,255,255,0.92)' },
                    }}
                  >
                    Start a School Pilot
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    component={Link}
                    to="/info"
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.3)',
                      '&:hover': { borderColor: 'rgba(255,255,255,0.6)', bgcolor: 'rgba(255,255,255,0.06)' },
                    }}
                  >
                    Explore the Product
                  </Button>
                  <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', textAlign: 'center' }}>
                    Works with Chrome Enterprise, Intune, and Jamf School.
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        </Reveal>
      </Container>
    </Box>
  );
}

export default LandingPage;
